/**
 * Product Size Chart Component
 * Supports middle-center popup, responsive table, IN/CM unit switcher,
 * global theme styles, keyboard navigation (ESC), and scroll lock.
 */
if (!customElements.get('m-size-chart')) {
  customElements.define(
    'm-size-chart',
    class MSizeChart extends HTMLElement {
      constructor() {
        super();
        this.trigger = this.querySelector('[data-size-chart-trigger]');
        this.modal = this.querySelector('[data-size-chart-modal]');
        this.isOpen = false;
        this.onKeyDown = this.handleKeyDown.bind(this);
        this.onTriggerClick = this.handleTriggerClick.bind(this);
      }

      connectedCallback() {
        if (!this.modal) return;

        // Teleport modal element to document.body so that fixed positioning is never
        // distorted or clipped by parent container transforms, animations, or overflow
        document.body.appendChild(this.modal);

        this.closeButtons = this.modal.querySelectorAll('[data-size-chart-close]');
        this.unitButtons = this.modal.querySelectorAll('[data-unit]');
        this.tables = this.modal.querySelectorAll('[data-table-unit]');

        this.bindEvents();
      }

      disconnectedCallback() {
        // Clean up when section is reloaded or removed in Shopify Theme Editor
        if (this.modal && this.modal.parentNode === document.body) {
          document.body.removeChild(this.modal);
        }
        document.removeEventListener('click', this.onTriggerClick);
        document.removeEventListener('keydown', this.onKeyDown);
        document.body.classList.remove('m-size-chart-open');
      }

      bindEvents() {
        // Global event delegation for all size chart triggers (inline or standalone)
        document.addEventListener('click', this.onTriggerClick);

        // Close triggers (X button and backdrop overlay)
        this.closeButtons.forEach((btn) => {
          btn.addEventListener('click', (e) => {
            e.preventDefault();
            this.close();
          });
        });

        // Unit switcher buttons (IN vs CM)
        this.unitButtons.forEach((btn) => {
          btn.addEventListener('click', (e) => {
            e.preventDefault();
            const unit = btn.dataset.unit;
            this.switchUnit(unit);
          });
        });
      }

      handleTriggerClick(e) {
        const trigger = e.target.closest('[data-size-chart-trigger], [data-open-sizeguide]');
        if (trigger) {
          e.preventDefault();
          this.open();
        }
      }

      open() {
        if (this.isOpen) return;

        this.modal.classList.remove('m:hidden');
        // Force reflow for smooth scale & opacity transition
        void this.modal.offsetHeight;

        this.modal.classList.add('is-active');
        document.body.classList.add('m-size-chart-open');
        this.isOpen = true;

        if (this.trigger) {
          this.trigger.setAttribute('aria-expanded', 'true');
        }

        // Focus modal dialog container for screen readers / keyboard users
        const dialog = this.modal.querySelector('.m-size-chart-modal__dialog');
        if (dialog) {
          dialog.focus();
        }

        document.addEventListener('keydown', this.onKeyDown);
      }

      close() {
        if (!this.isOpen) return;

        this.modal.classList.remove('is-active');
        document.body.classList.remove('m-size-chart-open');
        this.isOpen = false;

        if (this.trigger) {
          this.trigger.setAttribute('aria-expanded', 'false');
          this.trigger.focus();
        }

        document.removeEventListener('keydown', this.onKeyDown);

        // Hide element after CSS transition completes
        setTimeout(() => {
          if (!this.isOpen) {
            this.modal.classList.add('m:hidden');
          }
        }, 280);
      }

      handleKeyDown(e) {
        if (e.key === 'Escape' || e.key === 'Esc') {
          this.close();
        }
      }

      switchUnit(unit) {
        if (!unit) return;

        this.unitButtons.forEach((btn) => {
          const isActive = btn.dataset.unit === unit;
          btn.classList.toggle('is-active', isActive);
          btn.setAttribute('aria-selected', isActive ? 'true' : 'false');
        });

        this.tables.forEach((table) => {
          if (table.dataset.tableUnit === unit) {
            table.style.display = '';
          } else {
            table.style.display = 'none';
          }
        });
      }
    }
  );
}
